import { useEffect } from "react";
import { fileUrl } from "../../lib/cta";

export default function SiteHead({ seo, tracking, brand }) {
  useEffect(() => {
    // Title
    if (seo?.title) document.title = seo.title;

    const ensureMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs.matchAttrs || {}).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      Object.entries(attrs.setAttrs || {}).forEach(([k, v]) => el.setAttribute(k, v));
    };

    // Favicon
    if (brand?.favicon) {
      const href = fileUrl(brand.favicon);
      const ext = (brand.favicon.split(".").pop() || "").toLowerCase().split("?")[0];
      let mime = "image/png";
      if (ext === "svg") mime = "image/svg+xml";
      else if (ext === "ico") mime = "image/x-icon";
      else if (ext === "png") mime = "image/png";
      // Remove all existing favicon links
      document.head.querySelectorAll("link[rel~='icon']").forEach((l) => l.remove());
      // Insert fresh
      const link = document.createElement("link");
      link.rel = "icon";
      link.type = mime;
      link.href = href;
      document.head.appendChild(link);
      // Apple touch icon (PNG only)
      if (ext === "png") {
        const apple = document.createElement("link");
        apple.rel = "apple-touch-icon";
        apple.href = href;
        document.head.appendChild(apple);
      }
    }

    if (seo?.description) {
      ensureMeta('meta[name="description"]', {
        matchAttrs: { name: "description" },
        setAttrs: { content: seo.description },
      });
      ensureMeta('meta[property="og:description"]', {
        matchAttrs: { property: "og:description" },
        setAttrs: { content: seo.description },
      });
    }
    if (seo?.keywords) {
      ensureMeta('meta[name="keywords"]', {
        matchAttrs: { name: "keywords" },
        setAttrs: { content: seo.keywords },
      });
    }
    if (seo?.title) {
      ensureMeta('meta[property="og:title"]', {
        matchAttrs: { property: "og:title" },
        setAttrs: { content: seo.title },
      });
    }
    if (seo?.ogImage) {
      ensureMeta('meta[property="og:image"]', {
        matchAttrs: { property: "og:image" },
        setAttrs: { content: seo.ogImage },
      });
    }

    // Tracking scripts (only inject once per id)
    const injectScript = (id, content) => {
      if (document.getElementById(id)) return;
      const s = document.createElement("script");
      s.id = id;
      s.innerHTML = content;
      document.head.appendChild(s);
    };
    const injectExternal = (id, src) => {
      if (document.getElementById(id)) return;
      const s = document.createElement("script");
      s.id = id;
      s.async = true;
      s.src = src;
      document.head.appendChild(s);
    };

    if (tracking?.googleAnalyticsId) {
      injectExternal("ga-loader", `https://www.googletagmanager.com/gtag/js?id=${tracking.googleAnalyticsId}`);
      injectScript("ga-init", `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${tracking.googleAnalyticsId}');
      `);
    }
    if (tracking?.facebookPixelId) {
      injectScript("fb-pixel", `
        !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
        n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${tracking.facebookPixelId}');
        fbq('track', 'PageView');
      `);
    }
    if (tracking?.googleTagManagerId) {
      injectScript("gtm-init", `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${tracking.googleTagManagerId}');
      `);
    }
  }, [seo, tracking, brand]);

  return null;
}
